import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Container, Paper, Typography, IconButton, Button,
  Tooltip, Fade, CircularProgress, Chip, Avatar, Collapse,
  TextField, InputAdornment
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SendIcon from '@mui/icons-material/Send';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import KeyboardIcon from '@mui/icons-material/Keyboard';

// Status values
const STATUS = {
  LOADING: 'loading',        // Starting interview
  AI_SPEAKING: 'ai_speaking',
  IDLE: 'idle',              // Waiting for user to respond
  LISTENING: 'listening',    // Recording user speech
  PROCESSING: 'processing',  // Sending to AI
  AI_THINKING: 'ai_thinking' // AI is generating response
};

const STATUS_LABEL = {
  loading: 'Starting interview…',
  ai_speaking: 'Interviewer is speaking…',
  idle: 'Your turn — tap mic to respond',
  listening: 'Listening…',
  processing: 'Sending your response…',
  ai_thinking: 'Interviewer is thinking…'
};

export default function InterviewScreen({ profile, onEnd }) {
  const [messages, setMessages] = useState([]); // [{role:'assistant'|'user', content:''}]
  const [status, setStatus] = useState(STATUS.LOADING);
  const [showTypeInput, setShowTypeInput] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [speechSupported, setSpeechSupported] = useState(true);

  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const bottomRef = useRef(null);
  const isListeningRef = useRef(false);
  const finalTranscriptRef = useRef('');
  const ttsGenRef = useRef(0);

  // Scroll to bottom whenever messages update
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, interimText]);

  const stopSpeaking = useCallback(() => {
    ttsGenRef.current++;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  // Fetch TTS audio for a single chunk — returns object URL or null
  const fetchTTSChunk = useCallback(async (text, generation) => {
    try {
      const res = await fetch('/api/mi/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text })
      });
      if (!res.ok || ttsGenRef.current !== generation) return null;
      const blob = await res.blob();
      if (ttsGenRef.current !== generation) return null;
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }, []);

  // Play a single URL, returns promise that resolves when done
  const playURL = useCallback((url) => {
    return new Promise(resolve => {
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); audioRef.current = null; resolve(); };
      audio.onerror = () => { URL.revokeObjectURL(url); audioRef.current = null; resolve(); };
      audio.play().catch(resolve);
    });
  }, []);

  // Speak a complete text: splits into sentences, fires all TTS in parallel, plays in order
  const speak = useCallback(async (text, onDone) => {
    const generation = ++ttsGenRef.current;
    setStatus(STATUS.AI_SPEAKING);

    // Split into sentences
    const re = /[^.!?]*[.!?]+(?:\s|$)/g;
    const sentences = [];
    let match;
    let lastEnd = 0;
    while ((match = re.exec(text)) !== null) {
      const s = match[0].trim();
      if (s.length > 2) sentences.push(s);
      lastEnd = re.lastIndex;
    }
    const remainder = text.slice(lastEnd).trim();
    if (remainder.length > 2) sentences.push(remainder);
    if (sentences.length === 0) sentences.push(text);

    // Kick off all TTS fetches in parallel
    const urlPromises = sentences.map(s => fetchTTSChunk(s, generation));

    // Play in order
    for (const urlPromise of urlPromises) {
      if (ttsGenRef.current !== generation) return;
      const url = await urlPromise;
      if (!url || ttsGenRef.current !== generation) continue;
      await playURL(url);
    }

    if (ttsGenRef.current === generation) onDone && onDone();
  }, [fetchTTSChunk, playURL]);

  // Fetch AI response (streaming) and add to messages, with mid-stream TTS pipelining
  const getAIResponse = useCallback(async (messagesHistory) => {
    setStatus(STATUS.AI_THINKING);
    const generation = ++ttsGenRef.current;
    let aiText = '';

    // TTS pipeline: ordered list of fetch promises, consumer plays them in order
    const urlPromises = [];
    let ttsConsuming = false;
    let streamDone = false;

    function flushChunk(text) {
      const trimmed = text.trim();
      if (trimmed.length < 3) return;
      urlPromises.push(fetchTTSChunk(trimmed, generation));
      if (!ttsConsuming) {
        ttsConsuming = true;
        consumeTTS();
      }
    }

    async function consumeTTS() {
      setStatus(STATUS.AI_SPEAKING);
      let i = 0;
      while (true) {
        if (ttsGenRef.current !== generation) return;
        if (i < urlPromises.length) {
          const url = await urlPromises[i++];
          if (!url || ttsGenRef.current !== generation) continue;
          await playURL(url);
        } else if (streamDone) {
          break;
        } else {
          await new Promise(r => setTimeout(r, 40));
        }
      }
      if (ttsGenRef.current === generation) setStatus(STATUS.IDLE);
    }

    // Add placeholder message for streaming
    setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true }]);

    try {
      const response = await fetch('/api/mi/respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profile, messages: messagesHistory })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let pending = ''; // text not yet flushed to TTS

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              aiText += parsed.text;
              pending += parsed.text;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: aiText, streaming: true };
                return updated;
              });

              // Flush complete sentences from pending to TTS
              const re = /[^.!?]*[.!?]+(?:\s|$)/g;
              let match;
              let lastEnd = 0;
              while ((match = re.exec(pending)) !== null) {
                flushChunk(match[0]);
                lastEnd = re.lastIndex;
              }
              pending = pending.slice(lastEnd);
            }
          } catch {}
        }
      }

      // Flush any remaining text after stream ends
      if (pending.trim().length > 2) flushChunk(pending);
      streamDone = true;
      if (!ttsConsuming) { ttsConsuming = true; consumeTTS(); }

      // Finalize message
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: aiText, streaming: false };
        return updated;
      });
    } catch (err) {
      console.error(err);
      streamDone = true;
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: '[Error getting response. Please try again.]', streaming: false };
        return updated;
      });
      setStatus(STATUS.IDLE);
    }
  }, [profile, fetchTTSChunk, playURL]);

  // Start the interview
  useEffect(() => {
    let cancelled = false;

    async function startInterview() {
      try {
        const res = await fetch('/api/mi/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profile })
        });
        const data = await res.json();
        if (cancelled) return;

        const greeting = data.message;
        const initialMessages = [
          { role: 'user', content: '[START_INTERVIEW]' },
          { role: 'assistant', content: greeting }
        ];
        setMessages(initialMessages);
        speak(greeting, () => { if (!cancelled) setStatus(STATUS.IDLE); });
      } catch (err) {
        console.error(err);
        if (!cancelled) setStatus(STATUS.IDLE);
      }
    }

    startInterview();
    return () => { cancelled = true; stopSpeaking(); };
  }, []); // eslint-disable-line

  // Set up speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechSupported(false);
      setShowTypeInput(true);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += t;
        else interim += t;
      }
      if (final) finalTranscriptRef.current += final;
      setInterimText(interim);
    };

    recognition.onerror = (event) => {
      if (event.error !== 'no-speech') console.error('speech error:', event.error);
    };

    recognitionRef.current = recognition;
    return () => recognition.abort();
  }, []);

  function startListening() {
    if (!recognitionRef.current || isListeningRef.current) return;
    stopSpeaking();
    finalTranscriptRef.current = '';
    setInterimText('');
    isListeningRef.current = true;
    setStatus(STATUS.LISTENING);
    recognitionRef.current.start();
  }

  function stopListeningAndSend() {
    if (!isListeningRef.current) return;
    isListeningRef.current = false;
    recognitionRef.current?.stop();
    setInterimText('');

    // Small delay to let final results come through
    setTimeout(() => {
      const text = finalTranscriptRef.current.trim();
      if (text) sendUserMessage(text);
      else setStatus(STATUS.IDLE);
    }, 300);
  }

  function sendUserMessage(text) {
    if (!text.trim()) return;
    setStatus(STATUS.PROCESSING);
    setTypedText('');

    setMessages(prev => {
      const updated = [...prev, { role: 'user', content: text }];
      // Build API messages — filter out display-only fields, and exclude [START_INTERVIEW] placeholder
      const apiMessages = updated
        .filter(m => m.content !== '[START_INTERVIEW]')
        .map(m => ({ role: m.role, content: m.content }));
      getAIResponse(apiMessages);
      return updated;
    });
  }

  function handleEndInterview() {
    stopSpeaking();
    recognitionRef.current?.abort();
    // Filter out the [START_INTERVIEW] sentinel before passing to feedback
    const cleanTranscript = messages
      .filter(m => m.content !== '[START_INTERVIEW]' && m.content.trim())
      .map(m => ({ role: m.role, content: m.content }));
    onEnd(cleanTranscript);
  }

  const canInteract = status === STATUS.IDLE || status === STATUS.LISTENING;

  return (
    <Fade in>
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        {/* Header */}
        <Paper elevation={0} sx={{
          px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid', borderColor: 'divider', borderRadius: 0
        }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {profile.interviewerName || 'Alex'} — {profile.company}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {profile.role} · {profile.interviewType} interview
            </Typography>
          </Box>
          <Button
            variant="outlined" color="error" size="small"
            startIcon={<StopCircleIcon />}
            onClick={handleEndInterview}
            sx={{ borderRadius: 2 }}
          >
            End Interview
          </Button>
        </Paper>

        {/* Messages */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
          <Container maxWidth="md" disableGutters>
            {status === STATUS.LOADING && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 4, justifyContent: 'center' }}>
                <CircularProgress size={20} />
                <Typography color="text.secondary">Preparing your interviewer…</Typography>
              </Box>
            )}

            {messages
              .filter(m => m.content !== '[START_INTERVIEW]')
              .map((msg, i) => (
                <MessageBubble key={i} message={msg} interviewerName={profile.interviewerName || 'Alex'} />
              ))
            }

            {/* Interim speech text */}
            {interimText && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, px: 1 }}>
                <Box sx={{
                  maxWidth: '75%', px: 2, py: 1.5, borderRadius: 3,
                  bgcolor: 'rgba(99,102,241,0.15)', border: '1px dashed',
                  borderColor: 'primary.main'
                }}>
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    {interimText}
                  </Typography>
                </Box>
              </Box>
            )}

            <div ref={bottomRef} />
          </Container>
        </Box>

        {/* Status bar */}
        <Box sx={{
          textAlign: 'center', py: 0.75,
          bgcolor: 'background.paper',
          borderTop: '1px solid', borderColor: 'divider'
        }}>
          <Typography variant="caption" color={
            status === STATUS.LISTENING ? 'error.main' :
            status === STATUS.AI_SPEAKING ? 'secondary.main' :
            'text.secondary'
          } fontWeight={500}>
            {STATUS_LABEL[status] || ''}
          </Typography>
        </Box>

        {/* Controls */}
        <Paper elevation={0} sx={{
          px: 3, py: 2.5, borderTop: '1px solid', borderColor: 'divider',
          borderRadius: 0, bgcolor: 'background.paper'
        }}>
          <Container maxWidth="md" disableGutters>
            {/* Type input */}
            <Collapse in={showTypeInput}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth size="small" multiline maxRows={4}
                  placeholder="Type your response…"
                  value={typedText}
                  onChange={e => setTypedText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (typedText.trim() && canInteract) sendUserMessage(typedText);
                    }
                  }}
                  disabled={!canInteract}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <IconButton
                  color="primary" disabled={!typedText.trim() || !canInteract}
                  onClick={() => sendUserMessage(typedText)}
                  sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Collapse>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              {/* Toggle type input */}
              {speechSupported && (
                <Tooltip title={showTypeInput ? 'Hide text input' : 'Type instead'}>
                  <IconButton size="small" onClick={() => setShowTypeInput(v => !v)} color={showTypeInput ? 'primary' : 'default'}>
                    <KeyboardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Stop speaking */}
              {status === STATUS.AI_SPEAKING && (
                <Tooltip title="Skip — stop speaking">
                  <IconButton onClick={() => { stopSpeaking(); setStatus(STATUS.IDLE); }} color="secondary">
                    <VolumeUpIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* Mic button */}
              {speechSupported && (
                <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {status === STATUS.LISTENING && (
                    <Box sx={{
                      position: 'absolute', width: 80, height: 80, borderRadius: '50%',
                      bgcolor: 'error.main', opacity: 0.15,
                      animation: 'pulse 1.2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)', opacity: 0.15 },
                        '50%': { transform: 'scale(1.4)', opacity: 0.08 },
                        '100%': { transform: 'scale(1)', opacity: 0.15 },
                      }
                    }} />
                  )}
                  <IconButton
                    onClick={status === STATUS.LISTENING ? stopListeningAndSend : startListening}
                    disabled={!canInteract && status !== STATUS.LISTENING}
                    sx={{
                      width: 64, height: 64,
                      bgcolor: status === STATUS.LISTENING ? 'error.main' : (canInteract ? 'primary.main' : 'action.disabledBackground'),
                      color: '#fff',
                      boxShadow: canInteract || status === STATUS.LISTENING ? '0 4px 24px rgba(99,102,241,0.5)' : 'none',
                      '&:hover': {
                        bgcolor: status === STATUS.LISTENING ? 'error.dark' : 'primary.dark',
                        transform: 'scale(1.05)'
                      },
                      '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                      transition: 'all 0.2s'
                    }}
                  >
                    {status === STATUS.LISTENING ? <MicOffIcon sx={{ fontSize: 28 }} /> : <MicIcon sx={{ fontSize: 28 }} />}
                  </IconButton>
                </Box>
              )}

              {(status === STATUS.AI_THINKING || status === STATUS.PROCESSING) && (
                <CircularProgress size={24} />
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
              {status === STATUS.LISTENING
                ? 'Tap mic again to send your response'
                : speechSupported
                ? 'Tap mic to speak, or use the keyboard icon to type'
                : 'Type your response above and press Enter or ↑'}
            </Typography>
          </Container>
        </Paper>
      </Box>
    </Fade>
  );
}

function MessageBubble({ message, interviewerName }) {
  const isAI = message.role === 'assistant';

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: isAI ? 'flex-start' : 'flex-end',
      mb: 2, px: 1,
      alignItems: 'flex-end', gap: 1
    }}>
      {isAI && (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: 16, mb: 0.5 }}>
          <SmartToyIcon fontSize="small" />
        </Avatar>
      )}
      <Box sx={{ maxWidth: '75%' }}>
        {isAI && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, mb: 0.25, display: 'block' }}>
            {interviewerName}
          </Typography>
        )}
        <Box sx={{
          px: 2, py: 1.5,
          borderRadius: isAI ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
          bgcolor: isAI ? 'background.paper' : 'primary.main',
          border: isAI ? '1px solid' : 'none',
          borderColor: 'divider',
          position: 'relative',
        }}>
          <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {message.content}
            {message.streaming && (
              <Box component="span" sx={{
                display: 'inline-block', width: 8, height: 14, ml: 0.5,
                bgcolor: 'currentColor', opacity: 0.7,
                animation: 'blink 1s step-end infinite',
                '@keyframes blink': { '0%,100%': { opacity: 0.7 }, '50%': { opacity: 0 } }
              }} />
            )}
          </Typography>
        </Box>
        {!isAI && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, mt: 0.25, display: 'block', textAlign: 'right' }}>
            You
          </Typography>
        )}
      </Box>
      {!isAI && (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 16, mb: 0.5 }}>
          <PersonIcon fontSize="small" />
        </Avatar>
      )}
    </Box>
  );
}
